import { match } from "ts-pattern";
import { dateTimeRecordToLocalisedDateTime } from "@visioni/schedules";
import type { AlarmControlView, NextTransition, NextTransitionReason } from "./model";

/**
 * User-facing button labels for the alarm control.
 *
 * Centralised here (alongside `describe`/`describeAdjustRow`) so the
 * component, prop-driven Storybook stories, connected stories, and any
 * future automated tests can all reference a single source of truth.
 * Renaming a button — say "Disarm" → "Stand down" — should be a one-line
 * edit here that propagates everywhere without breaking play() assertions.
 *
 * Keep this object as `as const` so the TypeScript types narrow to the
 * exact string literals (handy for `getByRole({ name: BUTTON_LABELS.disarm })`).
 */
export const BUTTON_LABELS = {
  arm: "Arm",
  disarm: "Disarm",
  resume: "Resume",
  adjust: "Adjust",
  silence: "Silence alarm",
  extendSilence: "Extend silence",
} as const;

export type ButtonLabel = (typeof BUTTON_LABELS)[keyof typeof BUTTON_LABELS];

/**
 * The accessibility label suffix appended to a button while its command is
 * in flight (the `PendingAwareButton` overlay). Centralised so play()
 * assertions and the component agree.
 */
export const PENDING_LABEL_SUFFIX = "(in progress)";

export const labelInProgress = (label: ButtonLabel): string => `${label} ${PENDING_LABEL_SUFFIX}`;

/**
 * A pair of short strings shown in the alarm control header for any given
 * view variant. Australian English.
 */
export type ViewDescription = {
  readonly title: string;
  readonly subtitle: string;
};

/**
 * Translate an {@link AlarmControlView} into the user-visible title/subtitle
 * pair. This is the single source of truth for copy — the legacy
 * `ArmDisarmControl`/`AdjustControl`/`formattedCameraArmedState` variants are
 * all consolidated here.
 */
export const describe = (view: AlarmControlView): ViewDescription =>
  match(view)
    .returnType<ViewDescription>()
    .with({ kind: "loading" }, () => ({ title: "Loading…", subtitle: "" }))
    .with({ kind: "error" }, (v) => ({
      title: "Couldn't load alarm status",
      subtitle:
        v.reason === "query" ? "Check your connection and try again" : "The server returned an unexpected response",
    }))
    .with({ kind: "unconfigured" }, () => ({
      title: "Not configured",
      subtitle: "Set up a schedule to start monitoring",
    }))
    .with({ kind: "scheduled" }, (v) => ({
      title: v.mode === "armed" ? "Armed" : "Disarmed",
      // Now subtitle says *why the camera is in this state right now*.
      // For `scheduled` that's always the routine — i.e. "Scheduled".
      // The next-transition preview moves to the Adjust row, where its
      // own subtitle says *why the next change is coming*.
      subtitle: "Scheduled",
    }))
    .with({ kind: "inException" }, (v) => ({
      title: v.mode === "armed" ? "Armed" : "Disarmed",
      // Title carries the resulting mode; subtitle just names the cause.
      // No need to repeat "arming"/"disarming" — title already says it.
      subtitle: `Exception: ${v.exception.reason}`,
    }))
    .with({ kind: "inOverride" }, (v) => ({
      // Just "Armed" / "Disarmed" — the mode is the headline; the
      // subtitle tells the user it's a manual override and how long
      // it'll stick. `overrideEndLocalLabel` is pre-formatted by
      // `deriveView` against the same `now` it used everywhere else
      // (see model.ts) so this branch is pure — no wall-clock reads.
      title: v.override.action === "armed" ? "Armed" : "Disarmed",
      subtitle: v.overrideEndLocalLabel === null ? "Indefinitely" : `Temporarily, until ${v.overrideEndLocalLabel}`,
    }))
    .with({ kind: "activated" }, (v) =>
      match(v.silence)
        .returnType<ViewDescription>()
        // No subtitle when not silenced — the "siren and strobes" claim
        // here was inaccurate (the actual response depends on the
        // configured `AlarmResponse`: monitoring/recording/sirens/strobes
        // can each be on or off). The component renders a red warning
        // icon plus the "Alarm activated" title; that already conveys
        // urgency without overstating the device behaviour.
        .with({ kind: "none" }, () => ({ title: "Alarm activated", subtitle: "" }))
        .with({ kind: "active" }, (s) => ({
          title: "Alarm activated",
          subtitle: `Silenced for ${s.minutesRemaining.toString()} ${s.minutesRemaining === 1 ? "min" : "mins"}`,
        }))
        .exhaustive(),
    )
    .with({ kind: "applying" }, (v) => ({
      title: v.direction === "arming" ? "Arming…" : "Disarming…",
      // The component renders a green spinning sync icon to the left of
      // this text; copy intentionally has no leading icon glyph.
      subtitle: "Applying changes",
    }))
    // `pending` keeps the underlying card's copy intact — the only visual
    // signal that something is in flight is the spinner on the active
    // button, rendered by the component. We deliberately do *not* say
    // "Arming…" here: that belongs to `applying` (server accepted, camera
    // catching up), a distinct phase of the state machine.
    .with({ kind: "pending" }, (v) => describe(v.underlying))
    .exhaustive();

/**
 * Copy for the "Adjust" row that sits under the main status row. The row
 * previews what the schedule will do next and hosts the Adjust button.
 *
 * - Title: `describeNextTransition(...)` when a next transition exists, or
 *   "No upcoming changes" when none.
 * - Subtitle: `describeWhyNext(reason)` — same vocabulary the *now*
 *   subtitle would use once the upcoming transition lands. So a
 *   scheduled card whose next flip is driven by an exception will read
 *   "Exception: Weekend works" on the next row, and an in-flight
 *   change anywhere will read "Applying changes" — exactly matching the
 *   `applying` variant's own now-subtitle.
 * - `adjustable`: true when pressing Adjust produces a meaningful mutation.
 *   On `scheduled`/`inException`/`applying` that means a next transition
 *   exists; on `inOverride` extending the override is always valid.
 *
 * Returns `null` for variants without anything to preview (loading,
 * error, unconfigured, activated).
 */
export type AdjustRowDescription = {
  readonly title: string;
  readonly subtitle: string;
  readonly adjustable: boolean;
};

export const describeAdjustRow = (view: AlarmControlView): AdjustRowDescription | null =>
  match(view)
    .returnType<AdjustRowDescription | null>()
    .with({ kind: "loading" }, () => null)
    .with({ kind: "error" }, () => null)
    .with({ kind: "unconfigured" }, () => null)
    .with({ kind: "activated" }, () => null)
    // While a command is in flight, keep the preview text identical to the
    // underlying view (the user just saw it, flipping copy is jarring). The
    // component hides the Adjust button while pending so `adjustable` here
    // doesn't actually get read — but returning the underlying value keeps
    // this function honest (same input → same shape).
    .with({ kind: "pending" }, (v) => describeAdjustRow(v.underlying))
    .with({ kind: "scheduled" }, (v) => describeAdjustRowFromTransition(v.nextTransition, v.timezone, false))
    .with({ kind: "inException" }, (v) => describeAdjustRowFromTransition(v.nextTransition, v.timezone, false))
    // For inOverride, "always adjustable" is true only when the
    // override has a finite end — pressing Adjust on a finite override
    // extends it. Indefinite overrides have no end to push forward, so
    // the only meaningful Adjust action is `adjust(nextTransition)` —
    // which is meaningful only when there *is* a next transition. With
    // no next transition either, the Adjust button hides. The
    // component picks the right `target` based on the same
    // finite-end check (see AlarmControl.tsx).
    .with({ kind: "inOverride" }, (v) =>
      describeAdjustRowFromTransition(v.nextTransition, v.timezone, v.override.period.end !== null),
    )
    .with({ kind: "applying" }, (v) => describeAdjustRowFromTransition(v.nextTransition, v.timezone, false))
    .exhaustive();

/**
 * Shared by the four variants that host an Adjust row. `alwaysAdjustable`
 * encodes the `inOverride` exception — extending an active override is
 * always valid, even with no upcoming scheduled change.
 */
const describeAdjustRowFromTransition = (
  nextTransition: NextTransition | null,
  timezone: string,
  alwaysAdjustable: boolean,
): AdjustRowDescription => {
  if (nextTransition === null) {
    // No subtitle when there's nothing upcoming. The previous fallback
    // ("Scheduled") was redundant with the title and actively misleading
    // on `inOverride` (the camera is *not* on the schedule there) — it
    // also broke the "next-row subtitle mirrors next-now subtitle" rule
    // that the rest of the function relies on. ControlRow renders a
    // title-only row cleanly when subtitle is empty.
    return {
      title: "No upcoming changes",
      subtitle: "",
      adjustable: alwaysAdjustable,
    };
  }
  return {
    title: describeNextTransition(nextTransition, timezone),
    subtitle: describeWhyNext(nextTransition.reason),
    adjustable: true,
  };
};

/**
 * Translate a {@link NextTransitionReason} into the subtitle string that
 * sits under the next-transition preview. Mirrors the *now* subtitle the
 * camera will show once the transition lands — so the user sees the same
 * label before and after.
 */
export const describeWhyNext = (reason: NextTransitionReason): string =>
  match(reason)
    .returnType<string>()
    .with({ kind: "routine" }, () => "Scheduled")
    // Same shape as `inException`'s now-subtitle — the next-row tells
    // the same story the now-row will once the upcoming transition
    // lands. The transition's resulting mode is already in the next-
    // row title (e.g. "Disarms Fri 6:00 PM"), so the action prefix is
    // redundant here too.
    .with({ kind: "exception" }, (r) => `Exception: ${r.description}`)
    .with({ kind: "override" }, () => "Manual override")
    .with({ kind: "default" }, () => "Default")
    // Mirrors the `applying` variant's now-subtitle exactly so the
    // "Applying changes" signal reads identically wherever it appears.
    .with({ kind: "applying" }, () => "Applying changes")
    .exhaustive();

/**
 * Format a {@link NextTransition} as e.g. "Disarms Fri 6:00 PM".
 *
 * "Arms" / "Disarms" is the product voice — direct and domain-specific
 * rather than the generic "Turns on" / "Turns off" the legacy
 * `useCameraAlarmPresentation` used. The only remaining "Turns on/off"
 * copy lives in the legacy Android `strings.xml`, which is out of scope
 * for this feature.
 */
export const describeNextTransition = (transition: NextTransition, timezone: string): string => {
  const dt = dateTimeRecordToLocalisedDateTime(transition.at, timezone);
  const day = dt.toFormat("ccc");
  const time = dt.toFormat("h:mm a");
  const verb = transition.to === "armed" ? "Arms" : "Disarms";
  return `${verb} ${day} ${time}`;
};
